import { action } from '@ember/object';
import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { type Task, task } from 'ember-concurrency';
import type MarketSnapshotsOverviewController from 'lumen/controllers/market/snapshots/overview';
import type MarketPerformanceDataItem from 'lumen/models/market-performance/data-item';
import type { MarketPerformanceMeta } from 'lumen/models/market-performance/data-item';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import { LOCAL_STORAGE_KEYS } from 'lumen/services/local-storage';
import type { Transition } from 'lumen/services/transition';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import {
  type DataPreferences,
  parseDataPreferences,
} from 'lumen/utils/constants/market-snapshots-constants';
import protectedRoute from 'lumen/utils/decorators/protected-route';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

export type MarketSnapshotsOverviewParams = {
  fromDate?: string;
  orderBy?: string;
  page?: number;
  pageSize?: number;
  toDate?: string;
};

type Model = {
  loadDataItemsTask: Task<
    { items: MarketPerformanceDataItem[]; meta: MarketPerformanceMeta },
    [string, string, string, number, number, string]
  >;
};

@protectedRoute({
  features: ['reservations.market.overview'],
})
export default class MarketSnapshotsOverviewRoute extends Route {
  queryParams = {
    fromDate: { refreshModel: true },
    toDate: { refreshModel: true },
    orderBy: { refreshModel: true },
    page: { refreshModel: true },
    pageSize: { refreshModel: true },
  };

  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service('local-storage') declare localStorage: Services['local-storage'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  loadDataItemsTask = task(
    { restartable: true },
    async (
      fromDate: string,
      toDate: string,
      orderBy: string,
      page: number,
      pageSize: number,
      sdlyColumn: string,
    ) => {
      try {
        const response = await this.store.query<MarketPerformanceDataItem>(
          'market-performance/data-item',
          {
            namespace: APIGATEWAY,
            typeKey: `reservations/market/${this.hotelAndMarketService.market.id}/performance`,
            view: 'overview',
            compare: `${sdlyColumn},ly,budget`,
            group_by: 'property',
            start_date: fromDate,
            end_date: toDate,
            market_id: this.hotelAndMarketService.market.id,
            order_by: orderBy,
            page,
            page_size: pageSize,
          },
        );

        return {
          items: response,
          meta: response.meta as unknown as MarketPerformanceMeta,
        };
      } catch (error) {
        void this.alerting.showErrorAlert();
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  get storedDataPreferences(): DataPreferences {
    const stored = this.localStorage.getItem(
      LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_DATA_PREFERENCES,
    );

    if (!stored) {
      return parseDataPreferences(null);
    }

    try {
      return parseDataPreferences(JSON.parse(stored));
    } catch {
      return parseDataPreferences(null);
    }
  }

  beforeModel(transition: Transition): void {
    this.transition.validateMarketSnapshotsQueryParams(
      transition,
      this.paramsFor(this.routeName),
    );
  }

  model(params: MarketSnapshotsOverviewParams): Model {
    const today = this.hotelAndMarketService.getLocalTime();

    const fromDate =
      params.fromDate ?? today.startOf('month').format('YYYY-MM-DD');
    const toDate = params.toDate ?? today.endOf('month').format('YYYY-MM-DD');

    void this.loadDataItemsTask.perform(
      fromDate,
      toDate,
      params.orderBy ?? 'name',
      params.page ?? 1,
      params.pageSize ?? 25,
      this.storedDataPreferences.sdlyColumn,
    );

    return {
      loadDataItemsTask: this.loadDataItemsTask,
    };
  }

  setupController(
    controller: MarketSnapshotsOverviewController,
    model: Model,
  ): void {
    super.setupController(controller, model);

    controller.dataPreferences = this.storedDataPreferences;
  }

  @action
  doRefresh(): void {
    this.refresh();
  }
}
